import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FiDollarSign, FiTrendingUp, FiPieChart, FiCreditCard, FiTarget, FiArrowRight } from "react-icons/fi";

import { ContentHeader, StatCard } from "../../components/v4";
import { accounts } from "../utils/accountConstants";
import useBudgetForecast from "../hooks/useBudgetForecast";
import useProcessSpendingData from "./spending/useProcessSpendData";
import api from "../../utils/api";

/**
 * Net Worth calculation — mirrors Accounts.jsx manualGroupConfig + metrics logic
 */
const manualGroupConfig = [
	{ name: "Cash", accounts: ["NL: Cash"] },
	{ name: "Checking", accounts: ["NL: Checking - D100", "Erin: PNC - Checking"] },
	{ name: "Business Checking", accounts: ["NL: Immerge Checking"] },
	{ name: "Savings", accounts: ["Erin: PNC - Savings", "NL: Immerge Saving", "NL: Home Saving - S000"] },
	{ name: "Credit Cards", accounts: ["NL: Chase - Sapphire Preferred", "NL: Chase - United Explorer", "NL: Chase - Freedom", "NL: Chase - Ink Bold Preferred", "NL: Chase - United Business", "Erin: Chase - Sapphire Preferred", "Erin: PNC - Points Credit Card", "Erin: PNC - CashBack Credit Card"] },
	{ name: "Credit Line", accounts: ["NL: Home - CL"] },
	{ name: "Tax Estimate", accounts: ["NL: Tax Estimate"] },
	{ name: "Investments", accounts: ["NL: COIN", "NL: VAN"] },
];

const assetCategories = ["Cash", "Checking", "Business Checking", "Savings", "Investments"];
const liabilityCategories = ["Credit Cards", "Credit Line", "Tax Estimate"];

const quickLinks = [
	{ to: "/budget-forecast", label: "Budget Forecast", description: "Monthly projections and end-of-year balance", icon: FiTrendingUp },
	{ to: "/spending-averages", label: "Spending Averages", description: "Analyze spending patterns by category", icon: FiPieChart },
	{ to: "/accounts", label: "Accounts", description: "View all account balances and details", icon: FiCreditCard },
	{ to: "/networth", label: "Net Worth", description: "Track net worth over time", icon: FiTarget },
];

function MoneyHome() {
	// ── Net Worth (mirrors Accounts.jsx) ──
	const [balanceData, setBalanceData] = useState([]);

	useEffect(() => {
		api.get("/money/AccountBalance/")
			.then((res) => setBalanceData(res.data))
			.catch((err) => console.error("Error loading balances:", err));
	}, []);

	const netWorth = useMemo(() => {
		if (!balanceData.length) return null;

		// Build account → latest value lookup
		const accountToTotal = {};
		accounts.forEach((item) => {
			const latestEntry = balanceData
				.filter((d) => d.account === item.account)
				.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
			accountToTotal[item.account] = latestEntry ? latestEntry.value : 0;
		});

		// Compute manual category totals, then assets vs liabilities
		let totalAssets = 0;
		let totalLiabilities = 0;

		manualGroupConfig.forEach((grp) => {
			const grpTotal = grp.accounts.reduce((sum, accName) => sum + (accountToTotal[accName] || 0), 0);
			if (assetCategories.includes(grp.name)) {
				totalAssets += grpTotal;
			} else if (liabilityCategories.includes(grp.name)) {
				totalLiabilities += Math.abs(grpTotal);
			}
		});

		return totalAssets - totalLiabilities;
	}, [balanceData]);

	// ── EOM Estimate (from budget forecast hook) ──
	const { eomEstimate, loading: forecastLoading } = useBudgetForecast({});

	// ── Spent This Month (from spending data hook) ──
	const now = new Date();
	const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
	const monthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;

	const { compiledData } = useProcessSpendingData({
		startDate: monthStart,
		endDate: monthEnd,
		categoriesToExclude: ["Erin Paycheck"],
	});

	const monthSpending = useMemo(() => {
		if (!compiledData || compiledData.length === 0) return null;

		const homeBusinesses = ["Home", "Other", "Apartment"];
		let total = 0;

		compiledData.forEach((biz) => {
			if (homeBusinesses.includes(biz.business)) {
				biz.categories.forEach((cat) => {
					total += Math.abs(cat.total || 0);
				});
			}
		});

		return total;
	}, [compiledData]);

	const currentMonthLabel = now.toLocaleString("default", { month: "long" });

	return (
		<div className="v4-page money-home section-home section-home--v4">
			<ContentHeader
				icon={<FiDollarSign />}
				title="Money"
				subtitle="Financial overview"
			/>

			<div className="v4-stats-row">
				<StatCard
					label="Net Worth"
					value={netWorth !== null ? Math.round(netWorth) : 0}
					prefix="$"
					animate={true}
				/>
				<StatCard
					label="EOM Estimate"
					value={!forecastLoading && eomEstimate ? Math.round(eomEstimate) : 0}
					prefix="$"
					animate={true}
				/>
				<StatCard
					label={`Spent in ${currentMonthLabel}`}
					value={monthSpending !== null ? Math.round(monthSpending) : 0}
					prefix="$"
					changeType="down"
					animate={true}
				/>
			</div>

			<div className="section-home__links">
				<h2 className="section-home__links-title">Quick Links</h2>
				<div className="section-home__links-grid">
					{quickLinks.map((link) => {
						const Icon = link.icon;
						return (
							<Link key={link.to} to={link.to} className="section-home__link-card">
								<div className="section-home__link-header">
									<Icon className="section-home__link-icon" />
									<span className="section-home__link-label">{link.label}</span>
									<FiArrowRight className="section-home__link-arrow" />
								</div>
								{link.description && (
									<p className="section-home__link-description">{link.description}</p>
								)}
							</Link>
						);
					})}
				</div>
			</div>
		</div>
	);
}

export default MoneyHome;
